import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Download, FileSpreadsheet, FileText, Users, Percent, 
  ArrowLeft, Calendar, CheckCircle, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExportPage = () => {
  const [exporting, setExporting] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportStatus, setExportStatus] = useState(null);

  // Définir les dates par défaut (année en cours)
  React.useEffect(() => {
    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    setStartDate(yearStart.toISOString().split('T')[0]);
    setEndDate(now.toISOString().split('T')[0]);
  }, []);

  const handleExport = async (exportType) => {
    setExporting(exportType);
    setExportStatus(null);
    
    try {
      const token = localStorage.getItem('token');
      let url = '';
      let filename = '';

      switch (exportType) {
        case 'invoices-csv':
          url = `${API_URL}/api/export/invoices/csv?start_date=${startDate}&end_date=${endDate}`;
          filename = `factures_${startDate}_${endDate}.csv`;
          break;
        case 'invoices-excel':
          url = `${API_URL}/api/export/invoices/excel?start_date=${startDate}&end_date=${endDate}`;
          filename = `factures_${startDate}_${endDate}.xlsx`;
          break;
        case 'clients':
          url = `${API_URL}/api/export/clients/csv`;
          filename = `clients_export.csv`;
          break;
        case 'vat':
          url = `${API_URL}/api/export/vat/csv?period=year`;
          filename = `tva_recap.csv`;
          break;
        case 'comptable':
          url = `${API_URL}/api/export/comptable/csv?start_date=${startDate}&end_date=${endDate}`;
          filename = `export_comptable_${startDate}_${endDate}.csv`;
          break;
        case 'fec':
          url = `${API_URL}/api/accounting/export/fec?start_date=${startDate}&end_date=${endDate}`;
          filename = `fec_export.txt`;
          break;
        default:
          return;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
        
        setExportStatus({ type: 'success', message: `Export "${filename}" téléchargé avec succès` });
      } else {
        const error = await response.json();
        setExportStatus({ type: 'error', message: error.detail || 'Erreur lors de l\'export' });
      }
    } catch (error) {
      setExportStatus({ type: 'error', message: 'Erreur de connexion au serveur' });
    } finally {
      setExporting(null);
    }
  };

  const exportOptions = [
    {
      id: 'invoices-csv',
      title: 'Factures (CSV)',
      description: 'Export de toutes vos factures au format CSV, compatible Excel et logiciels comptables',
      icon: FileText,
      color: 'blue',
      badge: 'Recommandé'
    },
    {
      id: 'invoices-excel',
      title: 'Factures (Excel)',
      description: 'Export formaté avec couleurs et totaux, prêt à l\'emploi',
      icon: FileSpreadsheet,
      color: 'emerald',
      badge: 'Pro'
    },
    {
      id: 'clients',
      title: 'Carnet clients',
      description: 'Export de tous vos clients avec coordonnées, SIREN et numéros de TVA',
      icon: Users,
      color: 'violet',
      badge: null
    },
    {
      id: 'vat',
      title: 'Récapitulatif TVA',
      description: 'Synthèse de la TVA collectée par taux, prête pour la déclaration',
      icon: Percent,
      color: 'amber',
      badge: null
    },
    {
      id: 'comptable',
      title: 'Export comptable complet',
      description: 'Factures + Clients + TVA en un seul fichier pour votre expert-comptable',
      icon: FileSpreadsheet,
      color: 'indigo',
      badge: 'Nouveau'
    },
    {
      id: 'fec',
      title: 'FEC (Fichier Écritures Comptables)',
      description: 'Format réglementaire français pour les contrôles fiscaux',
      icon: FileText,
      color: 'slate',
      badge: 'Légal'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', hover: 'hover:border-blue-400' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'hover:border-emerald-400' },
      violet: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', hover: 'hover:border-violet-400' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', hover: 'hover:border-amber-400' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'hover:border-indigo-400' },
      slate: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', hover: 'hover:border-slate-400' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6" data-testid="export-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/smartfacture">
          <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Export comptable</h1>
          <p className="text-slate-500 text-sm">Exportez vos données pour votre comptable</p>
        </div>
      </div>

      {/* Status Message */}
      {exportStatus && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          exportStatus.type === 'success' 
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {exportStatus.type === 'success' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <FileText className="h-5 w-5" />
          )}
          <span>{exportStatus.message}</span>
        </div>
      )}

      {/* Date Range Selector */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Calendar className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Période d'export</CardTitle>
              <CardDescription>Sélectionnez la période pour les exports</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date début</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="start-date"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">Date fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="end-date"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  const now = new Date();
                  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
                  setStartDate(monthStart.toISOString().split('T')[0]);
                  setEndDate(now.toISOString().split('T')[0]);
                }}
              >
                Ce mois
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const now = new Date();
                  const quarter = Math.floor(now.getMonth() / 3);
                  const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
                  setStartDate(quarterStart.toISOString().split('T')[0]);
                  setEndDate(now.toISOString().split('T')[0]);
                }}
              >
                Ce trimestre
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  const now = new Date();
                  const yearStart = new Date(now.getFullYear(), 0, 1);
                  setStartDate(yearStart.toISOString().split('T')[0]);
                  setEndDate(now.toISOString().split('T')[0]);
                }}
              >
                Cette année
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Options */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {exportOptions.map((option) => {
          const Icon = option.icon;
          const colors = getColorClasses(option.color);
          
          return (
            <Card 
              key={option.id}
              className={`border ${colors.border} ${colors.hover} transition-all cursor-pointer`}
              data-testid={`export-${option.id}`}
            >
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${colors.bg}`}>
                    <Icon className={`h-6 w-6 ${colors.text}`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-800">{option.title}</h3>
                      {option.badge && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${colors.text} border-current`}
                        >
                          {option.badge}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{option.description}</p>
                    <Button
                      className={`mt-4 w-full ${colors.bg} ${colors.text} hover:opacity-80`}
                      variant="ghost"
                      onClick={() => handleExport(option.id)}
                      disabled={exporting === option.id}
                    >
                      {exporting === option.id ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Export en cours...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Télécharger
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-800">Formats compatibles</h3>
              <p className="text-sm text-blue-600 mt-1">
                Les exports CSV sont compatibles avec Excel, Google Sheets, Ciel Compta, Sage, EBP, 
                et tous les logiciels comptables standards. Le format FEC respecte les normes 
                réglementaires françaises (Article A.47 A-1 du LPF).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportPage;
